import React, { useEffect } from 'react';
import {
  Controller, Path, useForm, useFormState,
} from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import DetailsHead from '../../../core/components/details-head/DetailsHead';
import Layout from '../../../core/components/layout/Layout';
import { useLocationHistoryState } from '../../../core/hooks/useLocationHistoryState';
import AddressModal from '../../../common/components/address/AddressModal';
import AdditionalInfo from './AdditionalInfo';
import VehicleCheckDecision from './VehicleCheckDecision';
import CommonVehicleCheckFields from './CommonVehicleCheckFields';
import AddressField from '../../../common/components/address/AddressField';
import { useVehicleCheckBeforeAttachmentUpload } from '../hooks/useVehicleCheckBeforeAttachUpload';
import { ErrorsEnum, RequiredFieldSchema } from '../../../core/enums/errorsEnum';
import Select from '../../../core/components/select/Select';
import VisualInspection from './VisualInspection';
import VehicleEquipment from './VehicleEquipment';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  markersListSelector,
  vehicleAddressDetailsSelector,
  vehicleCheckDetailsSelector,
  vehicleDetailsSelector,
} from '../../../store/selectors/vehiclesSelectors';
import { getVehicleByIdThunk } from '../../../store/thunks/vehicles/vehicleViewPageThunks';
import ButtonActions from '../../../core/components/button-actions/ButtonActions';
import {
  getHandoverVehicleCheckDecisionsThunk, getVehicleCheckTypesThunk,
  getVehicleTransportationMethodsThunk, getWeatherConditionsThunk,
} from '../../../store/thunks/coreThunk';
import {
  handoverVehicleCheckDecisionsSelector,
  isFetchingSelector,
  vehicleCheckTypesValuesSelector,
  vehicleTransportationMethodsSelector,
} from '../../../store/selectors/coreSelectors';
import { YesNoOptions } from '../../../core/utils/testData';
import {
  createVehicleCheckThunk, getVehicleCheckAttachmentsListThunk,
  getVehicleCheckByIdThunk,
} from '../../../store/thunks/vehicles/VehicleInspectionThunks';
import { guidGenerator } from '../../../core/utils/guidGenerator';
import { SaveVehicleCheckDto } from '../../vehicle-view-page/types/VehicleViewTypes';
import { useVehicleCheckFormHelper } from '../hooks/useVehicleCheckFormHelper';
import ImageMarkersComponent from '../../../core/components/image-markers-component/ImageMarkersComponent';
import DrawerComponent from '../../../core/components/drawer/DrawerComponent';
import { handoverDefaults, HandoverFields } from '../utils/HandoverVehicleCheckSchema';

type HandoverVehicleCheckProps = {
  readMode?: boolean,
}

const HandoverVehicleCheck: React.FC<HandoverVehicleCheckProps> = ({ readMode }) => {
  const { id, checkId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isFetching = useAppSelector(isFetchingSelector);
  const vehicleDetails = useAppSelector(vehicleDetailsSelector);
  const vehicleAddress = useAppSelector(vehicleAddressDetailsSelector);
  const transportationMethods = useAppSelector(vehicleTransportationMethodsSelector);
  const handoverVehicleCheckDecisions = useAppSelector(handoverVehicleCheckDecisionsSelector);
  const markersList = useAppSelector(markersListSelector);
  const { handover } = useAppSelector(vehicleCheckTypesValuesSelector);
  const vehicleCheckDetails = useAppSelector(vehicleCheckDetailsSelector);

  const { backHandle } = useLocationHistoryState(false);
  const {
    handleSubmit, control, reset, clearErrors, setValue,
  } = useForm<HandoverFields>({
    defaultValues: handoverDefaults,
    mode: 'all',
  });
  const { errors } = useFormState({ control });

  const {
    backUrl, checkDate, closeDrawer,
    cleanUpForm, cleanUpMarkersState, isAddressModal,
    setAddressModal, addressFields,
    setAddressFields, addressError,
    setAddressError, fileList, setFileList,
    locationHelper, numericKeys, yesNoKeys,
    yesNoSetValueHelper, setAddressFilledValues, setMarkersFilledList,
    isDrawerReadMode, isDrawerVisible, openMarkersDetails,
  } = useVehicleCheckFormHelper(reset, clearErrors, setValue, readMode, true);

  const { beforeUpload } = useVehicleCheckBeforeAttachmentUpload(fileList, setFileList);
  const captureScheme = document.getElementById('capture');

  const onSubmit = (values: HandoverFields) => {
    const { areAllRequiredFields, location } = locationHelper();

    if (areAllRequiredFields) {
      setAddressError(ErrorsEnum.REQUIRED);
    } else {
      let base64URL = '';
      captureScheme && html2canvas(captureScheme).then((canvas) => {
        base64URL = canvas.toDataURL('image/jpeg');
        const finalData = {
          vehicleCheckType: handover,
          mileageMiles: +values.mileageMiles,
          fuelLevelPercent: values.fuelLevelPercent,
          transportationMethod: values.transportationMethod,
          weatherConditions: values.weatherConditions,
          isExteriorClean: values.isExteriorClean === 1,
          isInteriorClean: values.isInteriorClean === 1,
          numberOfVanKeys: +values.numberOfVanKeys,
          hasTools: values.hasTools === 1,
          hasSpareWheel: values.hasSpareWheel === 1,
          notes: values.notes ? values.notes.trim() : null,
          isPdiCompleted: values.isPdiCompleted === 1,
          isValeted: values.isValeted === 1,
          handoverVehicleCheckDecision: values.handoverVehicleCheckDecision,
          ...location,
          markers: markersList.map(({ comment }, index) => ({
            comment: comment ? comment.trim() : null,
            ordinalNumber: index + 1,
          })),
          visualInspectionImageBase64: base64URL
            ? base64URL.replace(/^data:image\/[a-z]+;base64,/, '')
            : null,
        };

        const list = fileList.map((el) => ({
          fileName: el.name,
          uploadGuid: guidGenerator(),
          file: el,
        }));
        id && handover && dispatch(createVehicleCheckThunk({
          vehicleId: +id,
          vehicleCheckData: finalData as SaveVehicleCheckDto,
          attachmentsList: list,
          navigateToInspectionTab: () => {
            cleanUpForm();
            navigate(`/${backUrl}`);
          },
        }));
      });
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getVehicleByIdThunk({ vehicleId: +id }));
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (vehicleAddress && !readMode) {
      setAddressFields(vehicleAddress);
    }
    // eslint-disable-next-line
  }, [vehicleAddress, readMode]);

  useEffect(() => {
    dispatch(getVehicleCheckTypesThunk());
    dispatch(getHandoverVehicleCheckDecisionsThunk());
    dispatch(getWeatherConditionsThunk());
    dispatch(getVehicleTransportationMethodsThunk());
    return () => {
      cleanUpForm();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (readMode && checkId && id) {
      dispatch(getVehicleCheckByIdThunk({ vehicleId: +id, vehicleCheckId: +checkId }));
      dispatch(getVehicleCheckAttachmentsListThunk({
        vehicleId: +id,
        vehicleCheckId: +checkId,
        filters: { page: 1, pageSize: 20 },
      }));
    }
    // eslint-disable-next-line
  }, [readMode, id, checkId]);

  useEffect(() => {
    if (readMode && vehicleCheckDetails) {
      Object.entries(vehicleCheckDetails).forEach(([key, value]) => {
        if (yesNoKeys.includes(key)) {
          yesNoSetValueHelper(key, value as (boolean | null));
        } else if (key === 'notes') {
          setValue('notes', vehicleCheckDetails.notes || '');
        } else if (numericKeys.includes(key)) {
          setValue(key as Path<HandoverFields>, value !== null ? value.toString() : '');
        } else setValue(key as Path<HandoverFields>, value as (string | number | null | undefined));
      });
      setAddressFilledValues();
      setMarkersFilledList();
    }
    // eslint-disable-next-line
  }, [readMode, vehicleCheckDetails]);
  return (
    <Layout
      subHeader={
        <DetailsHead goBack={() => backHandle(backUrl)} isCreatePage>
          <div className="createHead">{`Handover check${checkDate}`}</div>
        </DetailsHead>
      }
    >
      <DrawerComponent
        isVisible={isDrawerVisible}
        onClose={closeDrawer}
        closable
        title="Markers state"
      >
        <ImageMarkersComponent
          cleanUpMarkersState={cleanUpMarkersState}
          isDrawerReadMode={isDrawerReadMode}
        />
      </DrawerComponent>
      <AddressModal
        isVisible={isAddressModal}
        onCancel={() => {
          setAddressModal(false);
        }}
        onSubmit={(values) => {
          setAddressFields(values);
          setAddressModal(false);
        }}
        isOptionalFields={false}
        title="Handover location"
        isExtendedFields
        addressFields={addressFields}
      />
      <form onSubmit={handleSubmit(onSubmit)} className="create-form checkForm">
        <section className="details-form">
          <h2 className="info-grid__title">Vehicle details</h2>
          <CommonVehicleCheckFields control={control} errors={errors} vehicleDetails={vehicleDetails} disabled={readMode} />
          <AddressField
            addressFields={addressFields}
            setAddressModal={(v) => {
              setAddressModal(v);
              setAddressError('');
            }}
            title="Location"
            addressError={addressError}
            disabled={readMode}
          />
          <Controller
            control={control}
            name="transportationMethod"
            rules={RequiredFieldSchema}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(val) => field.onChange(val)}
                label="Method of transportation"
                options={transportationMethods}
                error={errors.transportationMethod?.message}
                className="details-form__field--lg"
                disabled={readMode}
              />
            )}
          />
        </section>
        <VisualInspection
          control={control}
          errors={errors}
          markersList={markersList}
          disabled={readMode}
          openMarkersDetails={openMarkersDetails}
        />
        <VehicleEquipment control={control} errors={errors} disabled={readMode} />
        <section className="details-form vehicleCheck-section vehicleEquipment__extended-fields">
          <Controller
            control={control}
            name="isPdiCompleted"
            rules={RequiredFieldSchema}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(val) => field.onChange(val)}
                label="PDI completed"
                options={YesNoOptions}
                error={errors.isPdiCompleted?.message}
                disabled={readMode}
              />
            )}
          />
          <Controller
            control={control}
            name="isValeted"
            rules={RequiredFieldSchema}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(val) => field.onChange(val)}
                label="Valeted"
                options={YesNoOptions}
                error={errors.isValeted?.message}
                disabled={readMode}
              />
            )}
          />
        </section>
        <AdditionalInfo
          control={control}
          errors={errors}
          fileList={fileList}
          setFileList={setFileList}
          beforeUpload={beforeUpload}
          disabled={readMode}
        />
        <VehicleCheckDecision
          control={control}
          errors={errors}
          options={handoverVehicleCheckDecisions}
          disabled={readMode}
          fieldName="handoverVehicleCheckDecision"
        />
        {!readMode && <ButtonActions
          createLabel="Save"
          createType="submit"
          cancelLabel="Clear"
          cancelClick={() => cleanUpForm(true)}
          isLoading={isFetching}
          offsetTop
        />}
      </form>
    </Layout>
  );
};

export default HandoverVehicleCheck;
